import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  useGetIdentity,
  useList,
  useOne,
  useShow,
  useGo,
  useNotification,
  useCustomMutation,
  useCustom,
} from '@refinedev/core'
import { DeleteButton, Show } from '@refinedev/mui'
import {
  Stack,
  Box,
  Button,
  Alert,
  Tab,
  Tabs,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  IconButton,
  Chip,
  Divider,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatLongDateNZ } from '../../utils/formatDate'
import OppRegionManagement from '../../components/ManageOppRegions'
import FundingRoundManagement from '../../components/ManageFundingRounds'
import OpportunityRevisions from '../../components/OpportunityRevisions'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import { User } from '@/utils/auth/auth0'
import AdminComments from '../../components/AdminComments'
import { ProposedEditsPanel } from '../proposed-edits/ProposedEditsPanel'
import AssignUserOpportunityModal from '../../components/AssignUserOpportunityModal'
import ReverseScanResultsDialog from '../../components/ReverseScanResultsDialog'
import { OpportunityScanResponse } from '../../types/reverseScan'
import { renderTextWithLinks } from '../../utils/textUtils'
import QuestionDisplay from '../../components/QuestionDisplay'
import { StructuredQuestion } from '../../interfaces/questions'
import { ReferenceChange } from '../../interfaces/referenceChanges'
import {
  RefStatusInline,
  CheckLinksButton,
} from '../../components/ReferenceStatusInline'
import {
  findQuestionInRawText,
  getHighlightedSegments,
} from '../../utils/questionHighlight'

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Not specified'
  return formatLongDateNZ(dateString)
}

const getUpcomingFundingRounds = (fundingRounds: any[]): any[] => {
  if (!fundingRounds) return []

  const currentDate = new Date()
  currentDate.setHours(0, 0, 0, 0)

  // Filter to rounds that are still open or will open in the future
  const upcoming = fundingRounds.filter((round) => {
    // If has close date, check it's not in the past
    if (round.application_close_date) {
      const closeDate = new Date(round.application_close_date)
      if (closeDate < currentDate) return false
    }
    // If has open date in the future, include it
    if (round.application_open_date) {
      return true
    }
    // Include rounds with no dates (they might be relevant)
    return true
  })

  // Sort by application_open_date (earliest first), nulls last
  return upcoming.sort((a, b) => {
    if (!a.application_open_date && !b.application_open_date) return 0
    if (!a.application_open_date) return 1
    if (!b.application_open_date) return -1
    const dateA = new Date(a.application_open_date)
    const dateB = new Date(b.application_open_date)
    return dateA.getTime() - dateB.getTime()
  })
}

export const OpportunitiesShow: React.FC = () => {
  const go = useGo()
  const navigate = useNavigate()
  const { query } = useShow({
    errorNotification: (error) => {
      return {
        message: 'Error getting opportunity',
        description:
          error?.message || 'An error occurred while fetching the opportunity',
        type: 'error',
      }
    },
  })
  const { data, isFetching, isError } = query

  const { data: user } = useGetIdentity() as User

  const theme = useTheme()
  const lightOrDarkMode = theme.palette.mode

  const { open: openNotification } = useNotification()

  const [editingRegions, setEditingRegions] = useState(false)
  const [editingFundingRounds, setEditingFundingRounds] = useState(false)
  const [assignUserModalOpen, setAssignUserModalOpen] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [scanComplete, setScanComplete] = useState(false)
  const [showScanResults, setShowScanResults] = useState(false)
  const [scanResults, setScanResults] = useState<OpportunityScanResponse[]>([])

  const [tabValue, setTabValue] = useState(0)
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<
    number | null
  >(null)
  const rawQuestionsContainerRef = useRef<HTMLDivElement>(null)
  const highlightRef = useRef<HTMLSpanElement>(null)

  // Scroll to top when component mounts or opportunity changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [data?.data?.uuid])

  function a11yProps(index: number) {
    return {
      id: `opportunity-tab-${index}`,
      'aria-controls': `opportunity-tabpanel-${index}`,
    }
  }
  function handleTabChange(e: React.SyntheticEvent, newValue: number) {
    setTabValue(newValue)
  }

  function toggleRegionsEdit() {
    setEditingRegions(!editingRegions)
  }

  function toggleFundingRoundsEdit() {
    setEditingFundingRounds(!editingFundingRounds)
  }

  function toggleAssignUserEdit() {
    setAssignUserModalOpen(true)
  }

  const { mutate: scanForOrgs } = useCustomMutation<OpportunityScanResponse[]>()

  const opportunityUuid = data?.data?.uuid ?? null

  const {
    data: refChangesData,
    isLoading: refChangesLoading,
    refetch: refetchRefChanges,
  } = useCustom<ReferenceChange[]>({
    url: opportunityUuid
      ? `/opportunities/${opportunityUuid}/reference-changes`
      : '',
    method: 'get',
    queryOptions: { enabled: !!opportunityUuid },
  })

  const { mutate: triggerCheckLinks, isLoading: checkingLinks } =
    useCustomMutation()

  const refChangesByUrl = useMemo(() => {
    const map = new Map<string, ReferenceChange>()
    for (const item of refChangesData?.data ?? []) {
      map.set(item.url, item)
    }
    return map
  }, [refChangesData])

  function handleCheckLinks() {
    if (!opportunityUuid) return
    triggerCheckLinks(
      {
        url: `/opportunities/${opportunityUuid}/check-references-now`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          refetchRefChanges()
          openNotification?.({
            message: 'Link check complete',
            type: 'success',
          })
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to check links',
            type: 'error',
          })
        },
      },
    )
  }

  const structuredQuestions = data?.data?.structured_questions || []
  const isStructured = structuredQuestions.length > 0

  // Combine raw questions into a single string for highlighting
  const rawQuestionsText = useMemo(() => {
    if (!data?.data?.questions || data.data.questions.length === 0) return ''
    return data.data.questions.join('\n\n')
  }, [data?.data?.questions])

  // Calculate highlight range for the selected question
  const highlightRange = useMemo(() => {
    if (selectedQuestionIndex === null || !rawQuestionsText) return null
    const question = structuredQuestions[selectedQuestionIndex]
    if (!question) return null
    return findQuestionInRawText(question.question, rawQuestionsText)
  }, [selectedQuestionIndex, structuredQuestions, rawQuestionsText])

  // Get highlighted segments for rendering
  const highlightedSegments = useMemo(() => {
    return getHighlightedSegments(rawQuestionsText, highlightRange)
  }, [rawQuestionsText, highlightRange])

  // Scroll to highlighted text when it changes
  useEffect(() => {
    if (highlightRef.current && rawQuestionsContainerRef.current) {
      const container = rawQuestionsContainerRef.current
      const highlight = highlightRef.current

      // Calculate position relative to container
      const containerRect = container.getBoundingClientRect()
      const highlightRect = highlight.getBoundingClientRect()

      // Check if highlight is outside visible area
      const isAboveViewport = highlightRect.top < containerRect.top
      const isBelowViewport = highlightRect.bottom > containerRect.bottom

      if (isAboveViewport || isBelowViewport) {
        highlight.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }
  }, [highlightRange])

  function scanForOpportunities() {
    setScanning(true)
    setScanComplete(false)
    scanForOrgs(
      {
        url: `/opportunities/scan/${data?.data.uuid}`,
        method: 'post',
        values: data?.data.uuid,
      },
      {
        onSuccess: async (response) => {
          openNotification?.({
            message: 'Scan completed successfully',
            type: 'success',
          })
          setScanning(false)
          setScanComplete(true)
          setScanResults(response.data || [])
        },
        onError: () => {
          openNotification?.({
            message: 'Failed to run scan',
            type: 'error',
          })
          setScanning(false)
        },
      },
    )
  }

  const { data: regionData, isLoading: regionDataIsLoading } = useOne({
    resource: 'opportunity-regions',
    id: data?.data.uuid,
    queryOptions: {
      enabled: !!data?.data.uuid,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting opportunity regions',
        description:
          error?.message ||
          'An error occurred while fetching the opportunity regions',
        type: 'error',
      }
    },
  })

  const {
    data: opportunityAssignedTo,
    isLoading: opportunityAssignedToIsLoading,
  } = useOne({
    resource: 'assigned-opportunities',
    id: data?.data.uuid,
    queryOptions: {
      enabled: !!data?.data.uuid,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting opportunity assignee',
        description:
          error?.message ||
          'An error occurred while fetching the opportunity assignee',
        type: 'error',
      }
    },
  })

  const { data: regionNamesData, isLoading: regionNameIsLoading } = useList({
    resource: 'regions',
    pagination: { pageSize: 100 },
    filters: [
      {
        field: 'id',
        operator: 'eq',
        value: regionData?.data,
      },
    ],
    queryOptions: {
      enabled:
        regionData?.data && regionData?.data.length > 0 && !regionDataIsLoading,
    },

    errorNotification: (error) => {
      return {
        message: 'Error getting region names',
        description:
          error?.message || 'An error occurred while fetching region names',
        type: 'error',
      }
    },
  })
  const { data: iwiNamesData, isLoading: iwiNameIsLoading } = useList({
    resource: 'iwi',
    pagination: { pageSize: 100 },
    filters: [
      {
        field: 'id',
        operator: 'eq',
        value: data?.data.iwi,
      },
    ],
    queryOptions: {
      enabled: !!(data?.data.iwi && data?.data.iwi.length > 0 && !isFetching),
    },

    errorNotification: (error) => {
      return {
        message: 'Error getting iwi names',
        description:
          error?.message || 'An error occurred while fetching iwi names',
        type: 'error',
      }
    },
  })

  // Fetch funder regions when opportunity has no regions
  const { data: funderRegionsData, isLoading: funderRegionsIsLoading } = useOne(
    {
      resource: 'funder-regions',
      id: data?.data.funder_uuid,
      queryOptions: {
        enabled:
          !!data?.data.funder_uuid &&
          (!regionData?.data || regionData?.data.length === 0) &&
          !regionDataIsLoading,
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting funder regions',
          description:
            error?.message || 'An error occurred while fetching funder regions',
          type: 'error',
        }
      },
    },
  )

  const { data: funderRegionNamesData, isLoading: funderRegionNameIsLoading } =
    useList({
      resource: 'regions',
      pagination: { pageSize: 100 },
      filters: [
        {
          field: 'id',
          operator: 'eq',
          value: funderRegionsData?.data?.map((fr: any) => fr.region_id),
        },
      ],
      queryOptions: {
        enabled:
          funderRegionsData?.data &&
          funderRegionsData?.data.length > 0 &&
          !funderRegionsIsLoading,
      },

      errorNotification: (error) => {
        return {
          message: 'Error getting funder region names',
          description:
            error?.message ||
            'An error occurred while fetching funder region names',
          type: 'error',
        }
      },
    })

  const { data: funderIwiNamesData, isLoading: funderIwiNameIsLoading } =
    useList({
      resource: 'iwi',
      pagination: { pageSize: 100 },
      filters: [
        {
          field: 'id',
          operator: 'eq',
          value: data?.data.funder_iwi,
        },
      ],
      queryOptions: {
        enabled: !!(
          data?.data.inherit_iwi_from_funder &&
          data?.data.funder_iwi &&
          data?.data.funder_iwi.length > 0 &&
          !isFetching
        ),
      },

      errorNotification: (error) => {
        return {
          message: 'Error getting funder iwi names',
          description:
            error?.message ||
            'An error occurred while fetching funder iwi names',
          type: 'error',
        }
      },
    })

  const { data: fundingRoundsData, isLoading: fundingRoundsDataIsLoading } =
    useList({
      resource: 'funding-rounds',
      meta: {
        opportunity_uuid: data?.data.uuid,
      },
      queryOptions: {
        enabled: !!data?.data.uuid,
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting funding rounds',
          description:
            error?.message || 'An error occurred while fetching funding rounds',
          type: 'error',
        }
      },
    })

  const funderUuid = data?.data?.funder_uuid

  const isLoading =
    isFetching ||
    regionDataIsLoading ||
    fundingRoundsDataIsLoading ||
    opportunityAssignedToIsLoading ||
    (funderRegionsIsLoading &&
      !!data?.data.funder_uuid &&
      (!regionData?.data || regionData?.data.length === 0) &&
      !regionDataIsLoading) ||
    (funderRegionNameIsLoading &&
      funderRegionsData?.data &&
      funderRegionsData?.data.length > 0 &&
      !funderRegionsIsLoading) ||
    (regionNameIsLoading &&
      regionData?.data &&
      regionData?.data.length > 0 &&
      !regionDataIsLoading) ||
    (iwiNameIsLoading &&
      data?.data.iwi &&
      data?.data.iwi.length > 0 &&
      !isFetching) ||
    (funderIwiNameIsLoading &&
      data?.data.inherit_iwi_from_funder &&
      data?.data.funder_iwi &&
      data?.data.funder_iwi.length > 0 &&
      !isFetching)

  if (isError) return <div>Error</div>

  return (
    <Show
      // Keep the default title or set a generic one
      title={<Typography variant="h5">Opportunity Details</Typography>}
      isLoading={isLoading}
      // Add the custom back button to headerButtons
      headerButtons={({ defaultButtons }) => (
        <>
          {funderUuid && (
            <Button
              startIcon={<ArrowBackIcon />} // Keep the icon for visual cue
              onClick={() => {
                go({
                  to: `/funders/${funderUuid}`, // Remove '/show'
                  type: 'push',
                })
              }}
              variant="outlined"
              size="small"
              sx={{ mr: 1 }} // Add some margin if needed
            >
              Funder {/* Change button text */}
            </Button>
          )}
          {data?.data && (
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={() =>
                navigate('/opportunities/create', {
                  state: { copiedFrom: data.data },
                })
              }
              variant="outlined"
              size="small"
            >
              Copy
            </Button>
          )}
          {defaultButtons}
          <DeleteButton
            resource="opportunities"
            recordItemId={data?.data.uuid}
            meta={{
              updated_by:
                user?.name ||
                `${user?.given_name || ''} ${user?.family_name || ''}`,
            }}
            onSuccess={() => {
              go({
                to: `/funders/${data?.data.funder_uuid}`,
                type: 'push',
              })
            }}
          >
            Delete
          </DeleteButton>
        </>
      )}
    >
      <Box sx={{ borderBotton: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Opportunity Details" {...a11yProps(0)} />
          <Tab label="Revisions" {...a11yProps(1)} />
          <Tab label="Comments" {...a11yProps(2)} />
          <Tab label="Proposed Edits" {...a11yProps(3)} />
        </Tabs>
      </Box>

      {showScanResults && (
        <ReverseScanResultsDialog
          showScanResults={showScanResults}
          setShowScanResults={setShowScanResults}
          scanResults={scanResults}
          oppName={data?.data.fund_name}
          funderName={data?.data.funder_name}
        />
      )}

      {tabValue == 0 && (
        <Stack gap={1}>
          {assignUserModalOpen && (
            <AssignUserOpportunityModal
              open={assignUserModalOpen}
              onClose={() => setAssignUserModalOpen(false)}
              opportunity_uuid={data?.data.uuid}
              currentUser={user}
              currentAssignedUser={
                opportunityAssignedTo?.data?.assigned_to_user_uuid ?? ''
              }
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography variant="h5">{data?.data.fund_name}</Typography>
            <Typography
              variant="caption"
              sx={{
                fontStyle: 'italic',
                ml: 2,
                color: 'text.secondary',
              }}
            >
              {data?.data.funder_name}
            </Typography>
          </Box>

          {(() => {
            const status = data?.data.traffic_light_status
            const flag = data?.data.flag_for_updates
            const hasProfile = !!data?.data.profile_text?.trim()
            const alwaysOpen = data?.data.always_open
            const funderActive = data?.data.funder_active
            const hasRounds =
              alwaysOpen ||
              (fundingRoundsData?.data
                ? getUpcomingFundingRounds(fundingRoundsData.data).length > 0
                : true) // don't show missing while loading

            const colorMap: Record<string, string> = {
              green: '#4caf50',
              yellow: '#ff9800',
              red: '#f44336',
            }
            const labelMap: Record<string, string> = {
              green: 'Green — Can be included in matching',
              yellow: 'Yellow — Currently excluded from matching',
              red: 'Red — Always excluded from matching',
            }

            const missing: string[] = []
            if (status === 'yellow') {
              if (flag === 'Active') {
                if (!hasProfile) missing.push('Missing profile text')
                if (!hasRounds)
                  missing.push('No current or upcoming funding rounds')
              }
            } else if (status === 'red') {
              if (funderActive === false) missing.push('Funder is Inactive')
            }

            return (
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <FiberManualRecordIcon
                    sx={{
                      color: colorMap[status ?? ''] ?? '#9e9e9e',
                      fontSize: 32,
                    }}
                  />
                  <Typography variant="body2">
                    {status ? labelMap[status] : 'Status unknown'}
                  </Typography>
                  {missing.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {missing.map((reason) => (
                        <Chip
                          key={reason}
                          label={reason}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                {flag && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, color: 'text.secondary' }}
                  >
                    Flag: {flag}
                  </Typography>
                )}
              </Box>
            )
          })()}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {scanComplete ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={scanning}
                  onClick={scanForOpportunities}
                  sx={{ width: '100px', mr: 1 }}
                >
                  Re-scan
                </Button>
                <Button
                  variant="contained"
                  disabled={scanning}
                  size="small"
                  onClick={() => setShowScanResults(true)}
                  sx={{ width: '200px' }}
                >
                  View Match Results
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                size="small"
                disabled={scanning}
                onClick={scanForOpportunities}
                sx={{ width: '200px' }}
              >
                {scanning ? 'Scanning...' : 'Scan for Org Matches'}
              </Button>
            )}
          </Box>

          <Divider sx={{ mb: 1 }}>
            <Typography variant="h6"> Internal Notes and References</Typography>
          </Divider>

          <AdminComments
            adminComments={data?.data.admin_comments}
            funderUuid={null}
            opportunityUuid={data?.data.uuid}
            onlyShowLatest={true}
          />

          {opportunityAssignedTo?.data &&
          opportunityAssignedTo?.data.assigned_to_user ? (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                {' '}
                Assigned to: {opportunityAssignedTo?.data.assigned_to_user}
              </Typography>
              <IconButton size="small" onClick={() => toggleAssignUserEdit()}>
                <EditIcon />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                {' '}
                Unassigned
              </Typography>
              <IconButton size="small" onClick={() => toggleAssignUserEdit()}>
                <AddIcon />
              </IconButton>
            </Box>
          )}

          <Typography variant="body1" fontWeight="bold">
            {data?.data.flag_for_updates
              ? `Flagged: ${data.data.flag_for_updates}`
              : 'No flag for updates'}
          </Typography>

          {data?.data.next_check_in_date && (
            <>
              <Typography variant="body1" fontWeight="bold">
                Next Check-in Date
              </Typography>
              <Typography variant="body2">
                {formatLongDateNZ(data.data.next_check_in_date)}
              </Typography>
            </>
          )}

          <Typography variant="body1" fontWeight="bold">
            Questions Last Checked At
          </Typography>
          <Typography variant="body2">
            {data?.data.questions_last_checked_at
              ? new Date(
                  data.data.questions_last_checked_at,
                ).toLocaleDateString('en-NZ', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : 'Never'}
          </Typography>

          <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
            Email Address
          </Typography>
          {data?.data.email_address ? (
            <Typography variant="body2">{data.data.email_address}</Typography>
          ) : data?.data.funder_email_address ? (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              {data.data.funder_email_address} (inherited from funder)
            </Typography>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              None
            </Typography>
          )}

          <Typography variant="body1" fontWeight="bold">
            Internal Notes
          </Typography>
          <ReactMarkdown
            className={`markdown-${lightOrDarkMode}`}
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {data?.data.notes || 'No notes'}
          </ReactMarkdown>

          <Typography variant="body1" fontWeight="bold">
            Internal References
          </Typography>
          {data?.data.references ? (
            <Stack spacing={1}>
              {data.data.references
                .split('\n')
                .filter((url: string) => url.trim())
                .map((url: string, index: number) => (
                  <Box key={index}>
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontSize: '16px' }}
                    >
                      {url}
                    </Link>
                    {refChangesLoading ? (
                      <CircularProgress size={12} sx={{ ml: 1 }} />
                    ) : (
                      <RefStatusInline
                        item={refChangesByUrl.get(url) ?? null}
                      />
                    )}
                  </Box>
                ))}
            </Stack>
          ) : (
            <Typography variant="body2">No references</Typography>
          )}

          <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
            Opportunity Link <PublicOutlinedIcon sx={{ fontSize: 18 }} />
          </Typography>
          {data?.data.opportunity_link ? (
            <Box>
              <Link
                href={data.data.opportunity_link}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ fontSize: '16px' }}
              >
                {data.data.opportunity_link}
              </Link>
              {refChangesLoading ? (
                <CircularProgress size={12} sx={{ ml: 1 }} />
              ) : (
                <RefStatusInline
                  item={refChangesByUrl.get(data.data.opportunity_link) ?? null}
                />
              )}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle={'italic'}
            >
              No direct opportunity link - users will see the funder's website
              instead.
            </Typography>
          )}

          {opportunityUuid && (
            <Box sx={{ mt: 1.5 }}>
              <CheckLinksButton
                isChecking={checkingLinks}
                isDisabled={checkingLinks || refChangesLoading}
                onClick={handleCheckLinks}
              />
            </Box>
          )}

          <Divider>
            <Typography variant="h6"> Details</Typography>
          </Divider>

          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6"> Hard Filters</Typography>
            <Typography variant="caption">
              {' '}
              Used to filter opportunities in matching. <br />
              Maximum Rank is 4, used to cap generic opportunities from scoring
              very high in matching.
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight="bold">
            Gambling Proceeds
          </Typography>
          <Typography variant="body1">
            {data?.data.gambling_proceeds ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            Is International
          </Typography>
          <Typography variant="body1">
            {data?.data.is_international ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            Only Funds Registered Charities
          </Typography>
          <Typography variant="body1">
            {data?.data.only_funds_registered_charities ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            Is Relational
          </Typography>
          <Typography variant="body1">
            {data?.data.is_relational ? 'Yes' : 'No'}
          </Typography>

          <Typography variant="body1" fontWeight="bold">
            Maximum Rank is 4
          </Typography>
          <Typography variant="body1">
            {data?.data.max_rank_is_4 ? 'Yes' : 'No'}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            Tags
          </Typography>
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, mb: 1 }}
          >
            {data?.data.tags && data?.data.tags.length > 0 ? (
              data.data.tags.map((tag: string) => (
                <Chip key={tag} label={tag} size="small" />
              ))
            ) : (
              <Typography variant="body2">No tags</Typography>
            )}
          </Box>

          <Typography variant="body1" fontWeight="bold">
            Iwi
          </Typography>

          {/* Opportunity-specific iwi */}
          {iwiNamesData?.data && iwiNamesData.data.length > 0 ? (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight="medium" color="primary">
                Opportunity Iwi:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                {iwiNamesData.data.map((iwi: any, index: number) => (
                  <Typography key={iwi.id}>
                    {iwi.name}
                    {index < iwiNamesData.data.length - 1 ? ',' : ''}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : null}

          {/* Inherited iwi from funder */}
          {data?.data.inherit_iwi_from_funder &&
            funderIwiNamesData?.data &&
            funderIwiNamesData.data.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  color="secondary"
                >
                  Inherits from funder:
                </Typography>
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}
                >
                  {funderIwiNamesData.data.map((iwi: any, index: number) => (
                    <Typography key={iwi.id}>
                      {iwi.name}
                      {index < funderIwiNamesData.data.length - 1 ? ',' : ''}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

          {/* No iwi message */}
          {(data?.data.inherit_iwi_from_funder
            ? !data?.data.funder_iwi || data?.data.funder_iwi.length === 0
            : !data?.data.iwi || data?.data.iwi.length === 0) && (
            <Typography variant="body1" color="text.secondary">
              None
            </Typography>
          )}

          <Divider />
          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">Amount Details</Typography>
            <Typography variant="caption">
              {' '}
              Used to filter opportunities in matching.
              <br />
              Description should be used for Internal reference.
            </Typography>
          </Box>

          <Typography variant="body1" fontWeight="bold">
            Amount Description
          </Typography>
          <Typography variant="body1">
            {data?.data.amount || 'No amount specified'}
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            Amount Range
          </Typography>
          {data?.data.no_min_max_amount ? (
            <Typography variant="body1">
              No Minimum or Maximum Amount
            </Typography>
          ) : (
            <Box sx={{ mb: 1, display: 'flex', width: '100%', gap: 1 }}>
              {data?.data.min_amount && data?.data.max_amount
                ? `$${Number(
                    data.data.min_amount,
                  ).toLocaleString()} to $${Number(
                    data.data.max_amount,
                  ).toLocaleString()}`
                : data?.data.min_amount
                ? `Over $${Number(data.data.min_amount).toLocaleString()}`
                : data?.data.max_amount
                ? `Under $${Number(data.data.max_amount).toLocaleString()}`
                : 'No minimum or maximum amount range specified'}
            </Box>
          )}
          {!data?.data.amount &&
            !data?.data.min_amount &&
            !data?.data.max_amount &&
            !data?.data.no_min_max_amount && (
              <Alert severity="info" sx={{ mb: 1, width: '400px' }}>
                No amount specified - will assume any amount
              </Alert>
            )}
          {data?.data.amount &&
            !data?.data.min_amount &&
            !data?.data.max_amount &&
            !data?.data.no_min_max_amount && (
              <Alert severity="info" sx={{ mb: 1, maxWidth: '1000px' }}>
                No minimum or maximum amount range specified. To ensure this
                opportuntity is matched correctly, please specify an amount
                range. <br />
                If this opportunity is open to any amount you can specify this
                by ticking the "No min or max amount" checkbox.
              </Alert>
            )}
          <Divider />

          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">Location Details</Typography>
            <Typography variant="caption">
              {' '}
              Used to filter opportunities in matching.
            </Typography>
          </Box>

          <Box display="flex" width={'100%'} justifyContent="space-between">
            <Box sx={{ my: 1 }}>
              <Typography variant="body1" fontWeight="bold">
                Locations
              </Typography>

              {/* Opportunity-specific regions */}
              {regionData?.data && regionData.data.length > 0 ? (
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color="primary"
                  >
                    Opportunity Locations:
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      mt: 0.5,
                    }}
                  >
                    {(regionNamesData?.data || []).map((region: any) => {
                      const hierarchy = [
                        region.country,
                        region.region,
                        region.council,
                        region.major,
                        region.suburb,
                      ]
                        .filter(Boolean)
                        .join(' → ')
                      return (
                        <Tooltip
                          key={region.id}
                          title={hierarchy || ''}
                          placement="top"
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'white',
                                color: 'grey.700',
                                boxShadow: 1,
                                fontSize: '0.85rem',
                                maxWidth: 400,
                              },
                            },
                          }}
                        >
                          <Link
                            onClick={() => {
                              go({
                                to: {
                                  resource: 'regions',
                                  action: 'show',
                                  id: region.id,
                                },
                              })
                            }}
                            sx={{
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              '&:hover': { color: 'primary.dark' },
                            }}
                          >
                            {region.name}
                          </Link>
                        </Tooltip>
                      )
                    })}
                  </Box>
                </Box>
              ) : null}

              {/* Only show inherited regions if opportunity has no regions of its own */}
              {(!regionData?.data || regionData?.data.length === 0) &&
                funderRegionNamesData?.data &&
                funderRegionNamesData.data.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color="secondary"
                    >
                      Inherits from funder:
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      {funderRegionNamesData.data.map((region: any) => {
                        const hierarchy = [
                          region.country,
                          region.region,
                          region.council,
                          region.major,
                          region.suburb,
                        ]
                          .filter(Boolean)
                          .join(' → ')
                        return (
                          <Tooltip
                            key={region.id}
                            title={hierarchy || ''}
                            placement="top"
                            slotProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '0.85rem',
                                  bgcolor: 'white',
                                  color: 'grey.700',
                                  boxShadow: 1,
                                  maxWidth: 400,
                                },
                              },
                            }}
                          >
                            <Link
                              onClick={() => {
                                go({
                                  to: {
                                    resource: 'regions',
                                    action: 'show',
                                    id: region.id,
                                  },
                                })
                              }}
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': { color: 'secondary.dark' },
                              }}
                            >
                              {region.name}
                            </Link>
                          </Tooltip>
                        )
                      })}
                    </Box>
                  </Box>
                )}

              {/* No regions message */}
              {(!regionNamesData?.data || regionNamesData.data.length === 0) &&
                (!funderRegionNamesData?.data ||
                  funderRegionNamesData.data.length === 0) && (
                  <Typography variant="body1" color="text.secondary">
                    No locations specified
                  </Typography>
                )}
            </Box>

            <Button
              variant="contained"
              onClick={toggleRegionsEdit}
              sx={{ alignSelf: 'flex-start', maxHeight: '40px' }}
              size="large"
            >
              {editingRegions ? 'Close' : 'Manage Opportunity Locations'}
            </Button>
          </Box>
          {data?.data.uuid && editingRegions && (
            <Box bgcolor={'#f5f5f5'} borderRadius={4} p={2}>
              <Typography variant="h5" gutterBottom>
                Manage Locations for {data?.data.fund_name}
              </Typography>
              <OppRegionManagement opportunityUuid={data?.data.uuid} />
            </Box>
          )}

          <Divider />

          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">
              Timing Details <PublicOutlinedIcon sx={{ ml: 0.25 }} />
            </Typography>
            <Typography variant="caption">
              {' '}
              Description for Internal Use Only <br />
              Funding Rounds and/or Always Open flag used to filter
              opportunities in matching. <br />
              Upcoming funding rounds visible to Organisations in their match
              results as the Application Dates. <br />
              If always open, 'Always open' is visible in match results as the
              Application Dates.
            </Typography>
          </Box>

          <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
            {data?.data.always_open ? 'Marked as Always open' : ''}
          </Typography>

          <Typography variant="body1" fontWeight="bold">
            Timing Description
          </Typography>
          <ReactMarkdown
            className={`markdown-${lightOrDarkMode}`}
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {data?.data.timing || 'None specified'}
          </ReactMarkdown>

          <Box display="flex" width={'100%'} justifyContent="space-between">
            <Box sx={{ my: 1 }}>
              <Typography variant="body1" fontWeight="bold">
                Funding Rounds
              </Typography>
              {fundingRoundsData?.data && (
                <Box mt={1}>
                  {(() => {
                    const upcomingRounds = getUpcomingFundingRounds(
                      fundingRoundsData.data,
                    )
                    if (upcomingRounds.length === 0) {
                      return (
                        <Typography
                          variant="body2"
                          fontSize={'16px'}
                          color={data?.data.always_open ? 'none' : 'error'}
                        >
                          No current funding rounds specified
                        </Typography>
                      )
                    }

                    if (upcomingRounds.length >= 1) {
                      const round = upcomingRounds[0]
                      return (
                        <>
                          <Typography variant="body1" fontWeight="bold">
                            {upcomingRounds.length > 1
                              ? 'Upcoming Funding Rounds'
                              : 'Upcoming Funding Round'}
                          </Typography>
                          <Typography variant="body2">
                            Application Open:{' '}
                            {formatDate(round.application_open_date)}
                          </Typography>
                          <Typography variant="body2">
                            Application Close:{' '}
                            {formatDate(round.application_close_date)}
                          </Typography>
                          <Typography variant="body2">
                            Decision Date: {formatDate(round.decision_date)}
                          </Typography>
                        </>
                      )
                    }

                    return (
                      <Typography variant="body2">
                        {upcomingRounds.length} upcoming funding rounds
                        available
                      </Typography>
                    )
                  })()}
                </Box>
              )}
            </Box>

            <Button
              variant="contained"
              sx={{ alignSelf: 'flex-start' }}
              size="large"
              onClick={toggleFundingRoundsEdit}
            >
              {editingFundingRounds ? 'Close' : 'Manage Funding Rounds'}
            </Button>
          </Box>
          {data?.data.uuid && editingFundingRounds && (
            <Box bgcolor={'#f5f5f5'} borderRadius={4} p={2}>
              <Typography variant="h5" gutterBottom>
                Manage Funding Rounds for {data?.data.fund_name}
              </Typography>
              <FundingRoundManagement
                opportunityUuid={data?.data.uuid}
                fundingRoundsData={fundingRoundsData?.data}
              />
            </Box>
          )}

          <Divider />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {' '}
            Profile <PublicOutlinedIcon sx={{ ml: 0.25 }} />
          </Typography>
          <Typography variant="caption">
            {' '}
            Used to filter opportunities in matching. <br />
            Visible to Organisations in match results.
          </Typography>

          {data?.data.profile_text && data?.data.profile_text.trim() !== '' ? (
            <>
              <Accordion
                sx={{
                  boxShadow: 'none',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  my: 2,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  Profile
                </AccordionSummary>

                <AccordionDetails>
                  <ReactMarkdown
                    className={`markdown-${lightOrDarkMode}`}
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  >
                    {data?.data.profile_text}
                  </ReactMarkdown>
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <Box my={2}>
              <Alert severity="warning">
                No Profile has been generated yet. Click the Edit button to add
                or generate one.
              </Alert>
            </Box>
          )}

          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">Profile Data</Typography>
            <Typography variant="caption">
              {' '}
              Used to generate a Profile for matching.
            </Typography>
          </Box>
          <Typography variant="body1" fontWeight="bold">
            Description
          </Typography>

          <ReactMarkdown
            className={`markdown-${lightOrDarkMode}`}
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {data?.data.description || 'No description'}
          </ReactMarkdown>
          <Typography variant="body1" fontWeight="bold">
            Criteria
          </Typography>
          <ReactMarkdown
            className={`markdown-${lightOrDarkMode}`}
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {data?.data.criteria || 'No criteria specified'}
          </ReactMarkdown>
          <Typography variant="body1" fontWeight="bold">
            Exclusions
          </Typography>
          <ReactMarkdown
            className={`markdown-${lightOrDarkMode}`}
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {data?.data.exclusions || 'None specified'}
          </ReactMarkdown>

          <Divider />

          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">
              General Info <PublicOutlinedIcon sx={{ ml: 0.25 }} />
            </Typography>
            <Typography variant="caption">
              {' '}
              Visible to Organisations in match results.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Information about the opportunity that should be separated from
              profile text and matching, but is also important for users to know
              about the opportunity.
            </Typography>
          </Box>

          {data?.data.general_info ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #ccc',
                borderRadius: 1,
                p: 2,
                maxWidth: '800px',
                mb: 2,
              }}
            >
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {renderTextWithLinks(data?.data.general_info)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ mb: 2 }}>
              None specified
            </Typography>
          )}

          <Divider />

          <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6">
              Application Details <PublicOutlinedIcon sx={{ ml: 0.25 }} />
            </Typography>
            <Typography variant="caption">
              {' '}
              Visible to Organisations in application drafting. <br />
              Link to Application Page is visible to Organisations in email copy
              of application.
            </Typography>
          </Box>

          {data?.data.application_info && (
            <>
              <Typography variant="body1" fontWeight="bold">
                Application Info
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: '16px', whiteSpace: 'pre-wrap' }}
              >
                {renderTextWithLinks(data?.data.application_info)}
              </Typography>
            </>
          )}

          {data?.data.application_link ? (
            <>
              <Typography variant="body1" fontWeight="bold">
                Link to Application Page
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: '16px', whiteSpace: 'pre-wrap' }}
              >
                {renderTextWithLinks(data?.data.application_link) ||
                  'None specified'}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" fontWeight="bold">
                Link to Application Page
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '16px' }}>
                None specified
              </Typography>
            </>
          )}

          <Box
            sx={{
              my: 2,
              display: 'flex',
              flexDirection: structuredQuestions.length > 0 ? 'row' : 'column',
              gap: 2,
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                Raw Questions
              </Typography>
              <Box
                ref={rawQuestionsContainerRef}
                sx={{
                  maxHeight: structuredQuestions.length > 0 ? '600px' : 'none',
                  overflowY:
                    structuredQuestions.length > 0 ? 'auto' : 'visible',
                  pr: 1,
                  p: 2,
                  bgcolor: '#f5f5f5',
                  borderRadius: 1,
                }}
              >
                {data?.data.questions && data?.data.questions.length > 0 ? (
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      m: 0,
                    }}
                  >
                    {highlightedSegments.map((segment, idx) =>
                      segment.isHighlighted ? (
                        <span
                          key={idx}
                          ref={highlightRef}
                          style={{
                            backgroundColor: '#fff3cd',
                            borderRadius: '2px',
                            padding: '1px 0',
                            boxShadow: '0 0 0 2px #ffc107',
                          }}
                        >
                          {segment.text}
                        </span>
                      ) : (
                        <span key={idx}>{segment.text}</span>
                      ),
                    )}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '16px' }}
                    color="error"
                  >
                    No questions have been added yet
                  </Typography>
                )}
              </Box>
            </Box>

            {isStructured && (
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
                  Structured Questions
                </Typography>
                <Box
                  sx={{
                    maxHeight: '600px',
                    overflowY: 'auto',
                    pr: 1,
                  }}
                >
                  {structuredQuestions.map(
                    (question: StructuredQuestion, index: number) => (
                      <Box
                        key={question.index}
                        onClick={() =>
                          setSelectedQuestionIndex(
                            selectedQuestionIndex === index ? null : index,
                          )
                        }
                        sx={{
                          borderLeft:
                            selectedQuestionIndex === index
                              ? '3px solid #ffc107'
                              : '3px solid transparent',
                          backgroundColor:
                            selectedQuestionIndex === index
                              ? 'rgba(255, 193, 7, 0.1)'
                              : 'transparent',
                          pl: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        <QuestionDisplay question={question} />
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {!isStructured && (
            <>
              <Typography
                variant="body1"
                fontWeight="bold"
                sx={{ mt: 3, mb: 2 }}
              >
                Structured Questions
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                No structured questions. Please update this in the edit form.
              </Alert>
            </>
          )}
        </Stack>
      )}
      {tabValue == 1 &&
        data?.data.uuid &&
        !isFetching &&
        !regionDataIsLoading &&
        !fundingRoundsDataIsLoading && (
          <OpportunityRevisions opportunityUuid={data.data.uuid} />
        )}

      {tabValue == 2 && data?.data.uuid && (
        <AdminComments
          adminComments={data?.data.admin_comments}
          funderUuid={null}
          opportunityUuid={data?.data.uuid}
        />
      )}

      {tabValue == 3 && data?.data.uuid && (
        <ProposedEditsPanel opportunityUuid={data.data.uuid} />
      )}
    </Show>
  )
}
